<template>
  <div>
    <Modal
      v-model:open="open"
      :title="config?.title"
      :maskClosable="false"
      :confirmLoading="loading"
      @ok="config?.ok"
      @cancel="config?.cancel"
      :ok-text="t('userCenter.confirm')"
      :cancel-text="t('userCenter.cancel')"
      :class="`${showOTPInput ? 'otp-modal' : ''}`"
      :okButtonProps="okBtnProps as any"
    >
      <p v-if="!privateKey" class="pt-3">
        <ExclamationCircleFilled style="color: #4749ad" />
        <span class="ml-2" v-html="welcomeMessage"></span>
      </p>
      <p v-else-if="resetPassword" class="pt-3">
        <ExclamationCircleFilled style="color: #4749ad" />
        <span class="ml-2" v-html="changeMessage"></span>
      </p>
      <Form
        v-if="resetPassword || !privateKey"
        class="content"
        ref="setRef"
        :config-data="setform"
        autocomplete="off"
        :configLayout="{ labelCol: { span: 0 }, wrapperCol: { span: 24 } }"
      />
      <div v-else>
        <OTPInput @update:code="handleCodeUpdate" :color="color" />
        <Tooltip placement="rightTop">
          <template #title>
            <div>
              {{ t("userCenter.privateKey.authPasswordDescription") }}
              <a
                :style="{ color: '#797DFF', cursor: 'pointer' }"
                @click="navigateToUserInfo"
              >
                {{ t("userCenter.privateKey.personalInfoLink") }}
              </a>
              {{ t("userCenter.privateKey.resetInstructions") }}
            </div>
          </template>
          <p class="tooltip-box">
            <ExclamationCircleFilled
              :style="{
                marginRight: '4px',
                color: color === '#4749ad' ? color : '#ff9432',
              }"
            />
            <span>{{ t("userCenter.privateKey.whatIsAuthPassword") }}</span>
          </p>
        </Tooltip>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import type { ConfigDataProps, ExposeType } from "@/components/form";
import type { User } from "@/api/login";
import type { Result } from "@/plugins/axios";
import { useStore } from "@/store";
import { updateUserInfo } from "@/api/userCenter";
import { getUserInfo } from "@/api/login";
import { recover } from "@/plugins/keythereum";
import { message, Modal } from "ant-design-vue";
import { useRouter } from "@/router";
import { saveKeyChain } from "@/utils/hooks/systemKeychain";
import OTPInput from "@/components/OTPInput.vue";
import { ref, computed, reactive, toRaw, onMounted, nextTick } from "vue";
import { ExclamationCircleFilled } from "@ant-design/icons-vue";
import Form from "@/components/form/index.vue";
import { isRecord } from "@/utils/typeGuards";
import { useCrypto } from "@/plugins/crypto/hook";
import { t } from "@/plugins/i18n";
import { Tooltip } from "ant-design-vue";

interface Props {
  resetPassword: boolean;
  color?: string;
}
const showOTPInput = computed(() => {
  return !resetPassword && !!privateKey.value;
});
const prop = defineProps<Props>();

const router = useRouter();

const { resetPassword, color = "#4749ad" } = toRaw(prop);
const IS_ELECTRON = process.env.IS_ELECTRON;

const codeArray = ref(Array(6).fill(""));

const handleCodeUpdate = (newCode: string[]) => {
  console.log("newCode:", newCode);
  codeArray.value = newCode;
};
console.log("color:", color);

function six(value: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (/[^0-9]/g.test(value)) {
      return reject(t("userCenter.privateKey.onlyNumbers"));
    }

    if (value && (value as string).length !== 6)
      return reject(t("userCenter.privateKey.sixDigitsRequired"));

    return resolve();
  });
}

const store = useStore();

const privateKey = computed(
  () => (store.state.user.userInfo as User)?.privateKey
);

const userId = (store.state.user.userInfo as User)?.id;

const publicKey = (store.state.user.userInfo as User)?.credential;

console.log("userinfo", store.state.user.userInfo);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "screct", arg: string): void;
  (e: "password", arg: string): void;
}>();

const open = ref(true);
const loading = ref(false);
const setRef = ref<ExposeType>();
const welcomeMessage = computed(() =>
  t("userCenter.privateKey.welcomeMessage")
);
const changeMessage = computed(() => t("userCenter.privateKey.changeMessage"));

const okBtnProps = computed(() => ({
  style: { background: showOTPInput.value ? color : "#4749ad" },
}));

const navigateToUserInfo = () => {
  if (IS_ELECTRON) {
    window.open(`${process.env.VUE_APP_H5_DOMAIN}/userCenter/userInfo`);
  } else {
    open.value = false;
    emit("close");
    router.push("/userCenter/userInfo");
  }
};

onMounted(() => {
  // 使用nextTick确保DOM已经渲染完成
  nextTick(() => {
    if (resetPassword || !privateKey.value) {
      // 如果是设置密码的表单，聚焦到第一个密码输入框
      const passwordInput = document.querySelector(
        'input[type="password"]'
      ) as HTMLInputElement;
      if (passwordInput) {
        passwordInput.focus();
      }
    } else {
      // 如果是验证密码的OTP输入框，聚焦到第一个输入框
      const otpInputs = document.querySelectorAll(".otp-input");
      if (otpInputs.length > 0) {
        (otpInputs[0] as HTMLInputElement).focus();
      }
    }
  });
  // 监听页面前进/后退事件，关闭弹窗
  const closeOnNavigation = () => {
    open.value = false;
    emit("close");
  };
  window.addEventListener("popstate", closeOnNavigation);
});

const setConfig = {
  title: resetPassword
    ? t("userCenter.privateKey.setNewPassword")
    : t("userCenter.privateKey.setPassword"),
  type: "warning",
  message: t("userCenter.privateKey.setPasswordMessage"),
  ok: async () => {
    try {
      const res = await setRef.value?.validate();
      if (!res) return;
      const { encryptMessage } = await useCrypto();
      if (
        !publicKey ||
        typeof publicKey !== "string" ||
        publicKey.length < 64
      ) {
        throw new Error(t("userCenter.privateKey.passwordSetupFailed"));
      }
      let encryptResult: string;
      try {
        encryptResult = await encryptMessage(publicKey, res.password);
      } catch (encErr: unknown) {
        log.error("encryptMessage failed", encErr);
        const resultDesc =
          isRecord(encErr) && typeof encErr.resultDesc === "string"
            ? encErr.resultDesc
            : undefined;
        throw new Error(
          resultDesc || t("userCenter.privateKey.passwordSetupFailed")
        );
      }
      log.debug(
        "getPrivateKey encrypt password res",
        encryptResult?.slice(0, 10) + "..."
      );
      await updateUserInfo({
        id: userId,
        password: encryptResult,
      });
      const user = await getUserInfo();
      (
        store as unknown as { commit: (type: string, payload: unknown) => void }
      ).commit("user/setUserInfo", user);
      const keystore = user.privateKey;
      if (!keystore)
        return Promise.reject(t("userCenter.privateKey.passwordMismatch"));
      const privateKey = recover({
        password: res.password as string,
        keystore: JSON.parse(keystore),
      });
      // 先发送密码事件，再发送私钥事件
      emit("password", res.password as string);
      emit("screct", privateKey);
      // 敏感信息已移除：decryptPasswordToMessage 结果
      if (IS_ELECTRON) {
        saveKeyChain({
          userNo: (store.state.user.userInfo as User)?.userNo,
          privateKey,
        });
      }
      message.success(t("userCenter.privateKey.passwordSetSuccess"));
      return;
    } catch (e: unknown) {
      const errorFields =
        isRecord(e) && Array.isArray(e.errorFields) ? e.errorFields : undefined;
      if (errorFields && errorFields.length > 0) {
        return;
      }
      message.error(
        ((isRecord(e) && typeof e.resultDesc === "string"
          ? e.resultDesc
          : undefined) as Result["resultDesc"]) ||
          t("userCenter.privateKey.passwordSetupFailed")
      );
      log.error(e);
    }
  },
  cancel: async () => {
    open.value = false;
    emit("close");
  },
};

const setform = reactive<ConfigDataProps>({
  password: {
    label: t("userCenter.privateKey.setPassword"),
    value: undefined,
    antType: "inputPassword",
    rules: [
      {
        required: true,
        validator: async (_: unknown, value: string) => six(value),
        trigger: "blur",
      },
    ],
    bind: {
      placeholder: t("userCenter.privateKey.enterSixDigitPassword"),
      autocomplete: "off",
      maxlength: 6,
    },
  },
  repassword: {
    label: t("userCenter.privateKey.confirmPassword"),
    value: undefined,
    antType: "inputPassword",
    rules: [
      {
        required: true,
        validator: async (_: unknown, value: string) => {
          await six(value as string);
          if (value !== setform.password.value) {
            return Promise.reject(t("userCenter.privateKey.passwordMismatch"));
          }
          return Promise.resolve();
        },
        trigger: "blur",
      },
    ],
    bind: {
      placeholder: t("userCenter.privateKey.enterSixDigitPassword"),
      autocomplete: "off",
      maxlength: 6,
    },
  },
});

const putConfig = {
  title: t("userCenter.privateKey.enterPassword"),
  type: "info",
  message: t("userCenter.privateKey.enterPasswordMessage"),
  ok: async () => {
    try {
      const password = codeArray.value.join("");
      // 敏感信息已移除
      // 验证输入
      if (!password) {
        message.error(t("userCenter.privateKey.pleaseEnterPassword"));
        return;
      }
      if (password.length !== 6) {
        message.error(t("userCenter.privateKey.sixDigitsRequired"));
        return;
      }

      const res = recover({
        password,
        keystore: JSON.parse(privateKey.value || "{}"),
      });
      console.log("res:", res);
      // 先发送密码事件，再发送私钥事件
      emit("password", password);
      emit("screct", res);
      if (IS_ELECTRON) {
        saveKeyChain({
          userNo: (store.state.user.userInfo as User)?.userNo,
          privateKey: res,
        });
      }
    } catch (e) {
      message.error(t("userCenter.privateKey.incorrectPassword"));
    }
  },
  cancel: async () => {
    open.value = false;
    emit("close");
  },
};

const config = computed((() =>
  !resetPassword && privateKey.value ? putConfig : setConfig) as () => {
  title: string;
  type: "info" | "warning";
  message: string;
  ok: () => Promise<void>;
  cancel: () => Promise<void>;
});
</script>

<style scoped>
.content {
  margin-top: 20px;
}

.tooltip-box {
  cursor: pointer;
  display: inline-block;
  position: relative;
}
</style>
